import {remCalc} from '_themeUtils'
import type {ThemeBreakpoints} from '_uiComponents'

const secondLevelMenu = {
    SecondLevelMenu: {
        baseStyle: ({
            isAtTop,
            editorialLength,
            headerSize,
            lifestyle,
        }: {
            isAtTop: boolean
            editorialLength: number
            headerSize?: ThemeBreakpoints
            lifestyle: boolean
        }) => ({
            secondLevel: {
                w: {base: 'full', lg: '100dvw'},
                position: 'absolute',
                left: isAtTop ? {lg: '50%'} : {lg: '50%', '2xl': '48%'}, // Menu is not perfectly centered after scrolling
                transform: {lg: 'translateX(-50dvw)'},
                top: {base: 0, lg: 'unset'},
                mt: {lg: remCalc(2)},
                borderTop: {lg: `${remCalc(1)} solid`},
                borderTopColor: {lg: 'color.border.header.default'},
                right: 0,
                height: '0',
                overflow: {base: 'hidden', lg: 'auto'},
                gap: 0,
                opacity: 0,
                visibility: 'hidden',
                pointerEvents: 'all',
                transition: 'opacity .15s ease-in-out',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: 'color.background.white',
            },
            open: {
                visibility: 'visible',
                overflowY: {base: 'scroll', lg: 'auto'},
                maxHeight: `calc(100dvh - ${remCalc(81)})`,
                opacity: '1',
                zIndex: '2',
                height: {base: 'full', lg: 'auto'},
                transitionDelay: '.1s',
            },
            secondLevelBack: {
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                height: '100%',
                width: '100%',
                flex: 0,
                padding: {base: `0 ${remCalc(16)}`, lg: 0},
                cursor: 'pointer',
            },
            catalogShowAll: {
                flex: '1 1 100%',
                gap: 0,
                backgroundColor: 'white',
                padding: {base: `${remCalc(32)} ${remCalc(16)} ${remCalc(92)}`, lg: `${remCalc(20)} ${remCalc(16)} ${remCalc(12)}`},
                borderTop: '1px solid',
                borderTopColor: 'color.border.header.default',
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
            },
            firstLevelItemArrow: {
                visibility: {base: 'visible', lg: 'hidden'},
                display: {base: 'block', lg: 'none'},
            },
            // EDITORIAL
            editorial: {
                width: '100%',
                gap: 0,
                position: 'relative',
                display: 'flex',
                flexDirection: {base: 'column', lg: 'row'},
            },
            editorialPictureWrapper: {
                pb: {base: '64%', lg: 0},
            },
            editorialLinkBox: {
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                bg: 'color.background.alpha.dark.30',
            },
            // CATALOG
            catalog: {
                top: 0,
                width: '100%',
                alignItems: {base: 'flex-start', lg: 'stretch'},
                padding: 0,
                display: 'flex',
                flexDirection: {base: 'column', xl: 'row'},
                gap: 0,
                backgroundColor: 'color.background.white',
            },
            catalogContainer: {
                display: 'flex',
                flexDirection: {base: 'column', lg: 'row'},
                gap: {base: 0, lg: `${remCalc(16)}`},
                width: '100%',
                padding: {base: `0`, lg: `0 ${remCalc(24)}`, xl: `0 0 0 ${remCalc(79)}`},
            },
            catalogBox: {
                height: '100%',
                width: '100%',
                p: 0,
                py: {lg: 'spacing.6'},
                display: 'flex',
                flexDirection: 'column',
            },
            catalogLinkBox: {
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                width: '100%',
                p: 0,
                py: {lg: 'spacing.6'},
            },
            editorialContainer: {
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                backgroundColor: 'color.background.grey.100',
                padding: {
                    base: `0 ${lifestyle ? remCalc(8) : remCalc(24)}`,
                    lg: `${remCalc(24)}`,
                    xl: `${remCalc(24)} ${remCalc(74)} ${remCalc(24)} ${remCalc(32)}`,
                },
                flex: {lg: `0 0 ${editorialLength > 2 ? 41.6 : 33.3}%`},
            },
            catalogEditorialBox: {
                width: '100%',
                position: 'relative',
                display: 'flex',
                flexDirection: {base: 'column', lg: 'row'},
                gap: {base: 0, lg: `${remCalc(20)}`},
            },
            editorialWidth: {
                maxWidth: {base: '100%', lg: `${100 / editorialLength}%`},
                w: '100%',
                display: 'flex',
                flexDirection: 'column',
            },
            editorialTitle: {
                w: '100%',
                paddingTop: {base: `${remCalc(24)}`, lg: '0'},
                paddingBottom: {base: `${remCalc(12)}`, lg: `${remCalc(6)}`},
                display: 'block',
                justifyContent: {base: 'space-between'},
            },
            editorialPitcure: {
                position: 'relative',
                w: '100%',
                h: '0px',
                overflow: 'hidden',
                paddingBottom: {base: `${lifestyle ? '156%' : '56%'}`, lg: `${lifestyle ? '156%' : '57%'}`, xl: '154%'},
                cursor: 'pointer',
                marginBottom: `${remCalc(8)}`,
            },
            hiddenforMobile: {
                display: {base: 'none', lg: 'block'},
                visibility: {base: 'hidden', lg: 'visible'},
            },
            discoverMoreDesktop: {
                color: 'color.text.black',
            },
            subcategoryBox: {
                borderTop: {base: '1px solid', lg: 'none'},
                borderTopColor: 'color.border.header.default',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
                cursor: {base: 'pointer', lg: 'auto'},
                padding: {base: `0 ${remCalc(24)}`, lg: `0 0 ${remCalc(8)} 0`},
            },
            linkTextTitle: {
                _notFirst: {
                    mt: {base: 0, lg: 'spacing.6'},
                },
            },
            subcategoryBoxNoPadding: {
                padding: '0',
            },
            subcategoryBisBoxOpen: {
                position: 'fixed',
                bottom: 0,
                height: {base: `calc(100dvh - ${headerSize?.base}px)`, md: `calc(100dvh - ${headerSize?.md}px)`},
                left: 0,
                backgroundColor: 'color.background.white',
                zIndex: '2',
                visibility: 'visible',
                opacity: 1,
                display: 'flex',
                overflowY: 'auto',
                overflowX: 'hidden',
                maxWidth: {base: '100%', md: `${remCalc(400)}`},
            },
            subcategoryBisBox: {
                // mb: {base: 0, lg: `${remCalc(34)}`},
                padding: {base: 0, lg: `${remCalc(16)} 0 0 0`},
                flexDirection: 'column',
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
                width: '100%',
                visibility: {base: 'hidden', lg: 'visible'},
                opacity: {base: 0, lg: 1},
                display: {base: 'none', lg: 'block'},
                height: 'full',
            },
            subcategoryBisBoxContent: {
                padding: {base: `0 ${remCalc(24)} 0 ${remCalc(16)}`, lg: 0},
                width: '100%',
            },
            subcategoryBis: {
                borderTop: {base: '1px solid', lg: 'none'},
                borderTopColor: 'color.border.header.default',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
                padding: {base: `${remCalc(20)} 0 ${remCalc(20)} ${remCalc(10)}`, lg: `0 0 ${remCalc(14)} 0`},
            },
            // MIXED
            mixed: {
                width: '100%',
                display: 'flex',
                flexDirection: {base: 'column', xl: 'row'},
                alignItems: 'stretch',
                gap: 0,
                padding: {base: `0 ${remCalc(16)}`, lg: `${remCalc(24)} 0 0 0`},
                backgroundColor: 'color.background.white',
            },
            mixedCategoryContainer: {
                width: '100%',
                display: 'flex',
                flexDirection: {base: 'column', lg: 'row'},
                paddingLeft: {base: '0', lg: `${lifestyle ? remCalc(57) : '0'}`},
                gap: {base: 0, lg: `${lifestyle ? remCalc(30) : '0'}`},
            },
            mixedBox: {
                display: 'flex',
                flexDirection: 'column',
                gap: 0,
                width: '100%',
            },
            mixedBoxBis: {
                flexDirection: 'column',
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
                width: '100%',
                visibility: {base: 'hidden', lg: 'visible'},
                opacity: {base: 0, lg: 1},
                display: {base: 'none', lg: 'block'},
                height: 'full',
                padding: {base: `0 ${remCalc(16)}`, lg: `0  ${lifestyle ? '0' : remCalc(57)}`},
                order: {base: 1, lg: `${lifestyle ? 2 : 1}`},
            },
            mixedContent: {
                order: {base: 1, lg: 0},
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
                borderBottom: {base: '1px solid', lg: 'none'},
                borderBottomColor: 'color.border.header.default',
                padding: {base: 0, lg: `0 ${lifestyle ? '0' : remCalc(57)}`},
            },
            mixedBoxBisOpen: {
                position: 'fixed',
                left: 0,
                top: remCalc(81),
                w: {base: 'full', md: remCalc(400), lg: 'unset'},
                maxHeight: `calc(100dvh - ${remCalc(81)})`,
                backgroundColor: 'color.background.white',
                zIndex: '2',
                visibility: 'visible',
                opacity: 1,
                display: 'flex',
            },
            mixedPicture: {
                w: '100%',
                order: {base: 0, lg: 1},
            },
            mixedPictureBox: {
                position: 'relative',
                w: '100%',
                h: '0px',
                overflow: 'hidden',
                marginBottom: {base: 0, lg: 0},
                paddingBottom: {base: '56%', lg: `${lifestyle ? '66%' : '92%'}`, xl: `${lifestyle ? '66%' : '47%'}`},
                order: {base: 1, lg: `${lifestyle ? 1 : 2}`},
            },
            picturePadding: {
                pt: `${remCalc(24)}`,
            },
            mixedsubcategoryBis: {
                borderBottom: {base: '1px solid', lg: 'none'},
                borderBottomColor: 'color.border.header.default',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
                padding: {base: `${remCalc(16)} 0 ${remCalc(16)} ${remCalc(12)}`, lg: `${remCalc(16)} 0 0 0`},
            },
            mixedsubcategoryBisBorder: {
                borderTop: {base: '1px solid', lg: 'none'},
                borderTopColor: 'color.border.header.default',
            },
            editorialBox: {
                flex: 1,
            },
        }),

        variants: {
            catalogImageFive: ({headerSize}: {headerSize?: ThemeBreakpoints}) => ({
                catalogShowAll: {
                    borderTop: 'none',
                },
                editorialPictureWrapper: {
                    pb: {base: '64%', lg: '62%'},
                },
                mixed: {
                    padding: {base: `0 ${remCalc(16)}`, lg: '0 0 0 0'},
                },
                mixedBoxBis: {
                    padding: {base: '0 0', lg: `0  ${remCalc(57)}`},
                    overflow: {base: 'scroll', lg: 'hidden'},
                },
                mixedBoxBisOpen: {
                    top: {base: `${headerSize?.base}px`, lg: `${headerSize?.lg}px`},
                    maxHeight: {base: `calc(100dvh - ${headerSize?.base}px)`, lg: `calc(100dvh - ${headerSize?.lg}px)`},
                },
                mixedContent: {
                    order: {base: 1, lg: 1},
                },
                mixedPicture: {
                    order: {base: 0, lg: 0},
                },
                mixedPictureBox: {
                    paddingBottom: {base: '64%', lg: '62%', xl: '62%'},
                },
                picturePadding: {
                    pt: 0,
                    pb: `${remCalc(24)}`,
                },
                mixedsubcategoryBis: {
                    borderBottom: 'none',
                    padding: {base: 0, lg: `${remCalc(16)} 0 0 0`},
                },
            }),
        },
    },
}

export default secondLevelMenu
